import { Box, CardHeader, Stack, Typography } from '@mui/material'
import { ReactElement, useCallback, useEffect, useState } from 'react'
import { Form } from 'react-final-form'
import { useParams } from 'react-router'
import {
  Kap_Goal_Annual_Report_Insert_Input,
  Kap_Measure,
  Kap_Measure_Annual_Report,
  Mutation_Root,
  Query_Root,
} from 'src/@types/graphql'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { ROUTES } from 'src/routing/routes'
import {
  queryKapAnnualReportAndGoalReportAndMeasures,
  upsertKapGoalAnnualReportMutation,
} from 'src/screens/kap/implementation/milestone/details/annual-report/goals/kapMilestoneGoalQueries'

import { queryMilestoneById } from 'src/screens/shared/implementation/details/milestoneQueries'
import { usePermissionsForKapMilestones } from 'src/service/security/PermissionHook'
import { SaveAndBackButton, SaveButton } from 'src/shared/button/Buttons'
import { GOAL_CHAR, PROJECT, TEXT_LENGTH } from 'src/shared/constants/constants'
import {
  GOAL_MEASURE_RATING,
  MILESTONE_RESPONSIBLE_TYPE_TYPE,
  MILESTONE_STATUS_TYPE,
} from 'src/shared/constants/milestone-constants'
import { AutoCompleteField } from 'src/shared/form/control/AutoCompleteField'
import { HtmlEditorField } from 'src/shared/form/control/HtmlEditorField'
import { DirtyFormSpy } from 'src/shared/form/dirty/DirtyFormSpy'
import { createDecorators } from 'src/shared/form/utils/decorators'
import { maxChar, required } from 'src/shared/form/validation/validators'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { NotAuthorized } from 'src/shared/not-authorized/NotAuthorized'
import { HelpAndInstructions } from 'src/shared/presentation/HelpAndInstructions'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import { HtmlSanitizer } from 'src/shared/utils/HtmlSanitizer'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { useClient, useQuery } from 'urql'
import { KapMilestoneGoalRelatedMeasuresGrid } from 'src/screens/kap/implementation/milestone/details/annual-report/goals/KapMilestoneGoalRelatedMeasuresGrid'
import {
  mergeKapGoalMeasuresWithKapMeasureAnnualReport,
  sortKapMeasures,
} from 'src/screens/shared/measure/utils/KapMeasureUtils'
import { ExpandableCard } from 'src/shared/presentation/ExpandableCard'
import styled from 'styled-components'

interface KapGoalRatingForm {
  rating: string
  reason: string
}

const decorators = createDecorators()

const CardHeaderStyled = styled(CardHeader)`
  height: 3rem;
  display: flex;
  justify-content: center;
  flex-grow: 1;
  padding-right: ${({ theme }) => theme.spacing(3)};

  & .MuiCardHeader-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
`

export const KapMilestoneGoalReportEditPage = (): ReactElement => {
  const {
    programId: programIdString,
    milestoneId: milestoneIdString,
    milestonePath,
    goalId: goalIdString,
  } = useParams()
  const programId = parseInt(programIdString as string)
  const goalId = parseInt(goalIdString as string)
  const milestoneId = parseInt(milestoneIdString as string)
  const { getMessage } = useMessageSource()
  const navigate = useDelayedNavigate()
  const process = PROJECT.KAP
  const urqlClient = useClient()
  const notificationService = useNotificationService()

  const [goalTitle, setGoalTitle] = useState('')
  const [relatedGoalMeasures, setRelatedGoalMeasures] = useState<(Kap_Measure & Kap_Measure_Annual_Report)[]>([])

  const [{ data, error }] = useQuery<{ milestone_by_pk: Query_Root['milestone_by_pk'] }, { id: number }>({
    query: queryMilestoneById,
    variables: { id: milestoneId },
  })

  if (error) {
    notificationService.operationFailed()
  }
  const milestone = data?.milestone_by_pk
  const { loading, canModifyGoals } = usePermissionsForKapMilestones(
    programId,
    milestone?.status as MILESTONE_STATUS_TYPE,
    milestone?.responsible_type as MILESTONE_RESPONSIBLE_TYPE_TYPE,
  )

  const [initialValues, setInitialValues] = useState<KapGoalRatingForm>()

  const ratingOptions = Object.keys(GOAL_MEASURE_RATING).map((key) => ({
    label: `${getMessage(`label.goal.rating.${key}`)}`,
    value: key,
  }))

  const fetchKapGoalAnnualReportAndMeasuresByGoalAndMilestoneId = useCallback(async () => {
    const { data, error } = await urqlClient
      .query<
        {
          kap_goal_by_pk: Query_Root['kap_goal_by_pk']
          kap_goal_annual_report: Query_Root['kap_goal_annual_report']
          kap_measure_annual_report: Query_Root['kap_measure_annual_report']
        },
        { goalId: number; milestoneId: number }
      >(queryKapAnnualReportAndGoalReportAndMeasures, { goalId: goalId, milestoneId: milestoneId })
      .toPromise()

    if (
      error ||
      !data?.kap_goal_annual_report ||
      !data?.kap_goal_by_pk?.kap_goal_measures ||
      !data?.kap_measure_annual_report
    ) {
      throw new Error('Resource not found')
    }

    return {
      fetchedKapGoalAnnualReport: data.kap_goal_annual_report,
      fetchedKapGoalByPk: data.kap_goal_by_pk,
      fetchedKapGoalMeasures: data.kap_goal_by_pk.kap_goal_measures,
      fetchedKapMeasureAnnualReport: data.kap_measure_annual_report,
    }
  }, [goalId, milestoneId, urqlClient])

  useEffect(() => {
    const initData = async () => {
      try {
        const {
          fetchedKapGoalAnnualReport,
          fetchedKapGoalByPk,
          fetchedKapGoalMeasures,
          fetchedKapMeasureAnnualReport,
        } = await fetchKapGoalAnnualReportAndMeasuresByGoalAndMilestoneId()

        setInitialValues({
          rating: fetchedKapGoalAnnualReport[0]?.rating ?? '',
          reason: fetchedKapGoalAnnualReport[0]?.reason ?? '',
        })

        const goalIdCharacter = getMessage(`label.goal.id.character`)
        setGoalTitle(`${fetchedKapGoalByPk.name} (${goalIdCharacter}${fetchedKapGoalByPk.sort_number})`)

        const mergedMeasuresAndReports = mergeKapGoalMeasuresWithKapMeasureAnnualReport(
          fetchedKapGoalMeasures,
          fetchedKapMeasureAnnualReport,
        )
        setRelatedGoalMeasures(sortKapMeasures(mergedMeasuresAndReports))
      } catch (err) {
        notificationService.operationFailed()
      }
    }
    initData()
  }, [fetchKapGoalAnnualReportAndMeasuresByGoalAndMilestoneId, getMessage, notificationService])

  let submit: any = () => {}
  let formValid = false

  const onSave = (event: any) => {
    submit(event)
  }

  const onSaveAndBack = async (event: any) => {
    try {
      await submit(event)
      formValid && onBack()
    } catch (error) {
      // do nothing
    }
  }
  const onBack = () => {
    const route = ROUTES.KapAnnualReportMilestoneRoot.nested.GoalRating.params({
      programId,
      milestoneId,
      milestonePath,
    })

    navigate(`${route}#${GOAL_CHAR}${goalId}`, { state: { expandedEntityId: `${GOAL_CHAR}${goalId}` } })
  }

  const onSubmitLocal = async (values: KapGoalRatingForm) => {
    const goalKapAnnualReportModel = {
      milestone_id: milestoneId,
      kap_goal_id: goalId,
      rating: values.rating,
      reason: HtmlSanitizer.sanitize(values.reason),
    }

    const { data } = await urqlClient
      .mutation<
        { insert_kap_goal_annual_report_one: Mutation_Root['insert_kap_goal_annual_report_one'] },
        { object: Kap_Goal_Annual_Report_Insert_Input }
      >(upsertKapGoalAnnualReportMutation, { object: goalKapAnnualReportModel })
      .toPromise()

    if (data && data?.insert_kap_goal_annual_report_one?.id) {
      notificationService.changesSaved()
      setInitialValues(values)
    } else {
      notificationService.operationFailed()
    }
  }

  if (!loading && !canModifyGoals) {
    return <NotAuthorized inEditPage />
  }

  return (
    <>
      <ScreenLayout
        title={getMessage('label.edit.goal.milestone.title', [goalTitle])}
        onBack={onBack}
        hasSecondLevelNavigation={false}
        actions={
          <>
            <SaveAndBackButton origin="header" onClick={onSaveAndBack} />
            <SaveButton origin="header" onClick={onSave} />
          </>
        }
      >
        <PageLayout>
          <>
            {!loading && initialValues && (
              <>
                <HelpAndInstructions
                  labelKey={`label.help.milestone.goal.rating.${process.toLowerCase()}`}
                  defaultExpansion
                />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <Form<KapGoalRatingForm>
                    initialValues={initialValues}
                    onSubmit={onSubmitLocal}
                    decorators={decorators}
                    render={({ handleSubmit, valid }) => {
                      submit = handleSubmit
                      formValid = valid
                      return (
                        <form onSubmit={handleSubmit} noValidate>
                          <Stack spacing={2}>
                            <AutoCompleteField
                              name="rating"
                              label={getMessage('label.goal.rating')}
                              options={ratingOptions}
                              validate={required()}
                              required
                            />
                            <HtmlEditorField
                              name="reason"
                              label={getMessage('label.goal.rating.comment')}
                              validate={maxChar(TEXT_LENGTH.KAP_GOAL_ANNUAl_REPORT_REASON)}
                              required
                            />
                            <DirtyFormSpy />
                          </Stack>
                        </form>
                      )
                    }}
                  />
                  <ExpandableCard
                    header={
                      <CardHeaderStyled
                        title={<Typography variant="subtitle2">{getMessage('label.related.measures')}</Typography>}
                      />
                    }
                    content={<KapMilestoneGoalRelatedMeasuresGrid measures={relatedGoalMeasures} />}
                  />
                </Box>
              </>
            )}
          </>
        </PageLayout>
      </ScreenLayout>
    </>
  )
}
